import { CommonModule } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { Store, StoreModule } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { WINDOW, windowFactory } from 'src/app/feature/injection-token/window';

import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { validString } from 'src/app/shared/utilities/types.utils';
import { AppState } from '../../app.state';
import { postApiRequest } from '../api/api.actions';
import { setLanguage } from '../client-configuration/client-configuration.actions';
import { fetchLocaleFile, fetchLocaleFileOnClientConfigurationErrorAction } from '../locale/locale.actions';
import { LoggerService } from '../logger/logger.service';
import { selectMerchant } from '../merchant-details/merchant-details.selectors';
import { fullRouteTo, routeTo } from '../routing/routing.actions';
import { initializeFinished, initializeStart } from './initialize.actions';
import { initializeReducer } from './initialize.reducer';

interface PossibleInitialQueryParams {
	t?: string;
	_d?: string;
}

export function isInitialized({ initialize }: AppState): boolean {
	return initialize.applicationConfigLoaded && initialize.environmentLoaded && initialize.merchantLoaded;
}

@NgModule({
	declarations: [],
	imports: [CommonModule, StoreModule.forFeature('initialize', initializeReducer)],
	providers: [
		{
			provide: WINDOW,
			useFactory: windowFactory,
		},
		{
			deps: [LoggerService, Store, WINDOW],
			provide: APP_INITIALIZER,
			useFactory: initializeApp,
			multi: true,
		},
	],
})
export class InitializeModule {}

/**
 * Initializes the application
 * @param logger Injecting logger, so it can set the debug query param flag if needed
 * @param store$ The global store$
 * @param window The window object
 */
export function initializeApp(logger: LoggerService, store$: Store<AppState>, window: Window) {
	const queryParams = getQueryParams(window.location.search);
	logger.setEnabled(window, queryParams._d === 'true');

	return () =>
		new Promise((resolve) => {
			store$.dispatch(initializeStart());
			store$
				.select((state: AppState): AppState => state)
				.pipe(
					filter((state: AppState) => state.environment.loaded),
					take(1)
				)
				.subscribe((state: AppState) => {
					// If we don't have an tenant or merchant id, this is invalid.
					if (state.clientConfiguration.error) {
						const { language } = window.navigator;
						[
							setLanguage({ payload: language }),
							fetchLocaleFileOnClientConfigurationErrorAction({ payload: language }),
							fullRouteTo({ payload: ['invalid'] }),
						].forEach(store$.dispatch, store$);
						// @ts-ignore
						return resolve();
					}

					// Wait for the merchant details to load
					store$
						.select(selectMerchant)
						.pipe(
							filter((merchant) => merchant.loaded || merchant.errorLoading),
							take(1)
						)
						.subscribe((merch) => {
							// Load the language files
							const lang = state.clientConfiguration.language;
							if (lang !== 'en-US') {
								store$.dispatch(fetchLocaleFile({ payload: lang }));
							}

							if (merch.errorLoading) {
								store$.dispatch(routeTo({ payload: ['invalid'] }));
								store$.dispatch(initializeFinished());
								// @ts-ignore
								return resolve();
							}

							store$
								.select((appState: AppState): AppState => appState)
								.pipe(filter(filterAppInitialized), take(1))
								.subscribe(() => {
									store$.dispatch(initializeFinished());
									// @ts-ignore
									resolve();
								});
						});

					// Need these locales if there are any errors
					store$.dispatch(
						postApiRequest({
							requestType: ApiRequestType.GET_APPLICATION_LOCALE,
							params: {
								section_list: ['common', 'errorcode', 'error', 'login'].join(','),
							},
						})
					);
					// Fire off the get merchant details
					store$.dispatch(
						postApiRequest({
							requestType: ApiRequestType.GET_MERCHANT_DETAILS,
							params: {
								include_characs: '1',
							},
						})
					);
				});
		});
}

/**
 * Find out if the application is initialized or not.
 * @param appState The application state
 */
function filterAppInitialized(appState: AppState) {
	return appState.initialize.initialized;
}

/**
 * Tries to find if we have a query param `t` which is the InitializeAccessoPaySession token to fetch params when
 * loading accesso Pay through a native application.
 * @param params The query param string
 */
export function getQueryParams(params: string): PossibleInitialQueryParams {
	if (validString(params)) {
		const cleanedParams: string = params.charAt(0) === '?' ? params.substring(1) : params;
		return cleanedParams.split('&').reduce((acc, section) => {
			const [key, value] = section.split('=');
			acc[decodeURIComponent(key)] = decodeURIComponent(value);

			return acc;
		}, {});
	}

	return {};
}
